import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ImportExportData, ImportExportFormat, ImportExportKind, ImportRequest } from './types';

const KEY = ['ref-network-import-export'];
const URL = '/api/ref-network-import-export';

async function call(path: string, init?: RequestInit) {
    const response = await fetch(`${URL}${path}`, init);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as ImportExportData;
}

const json = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const isRunning = (data?: ImportExportData) => !!data && [...data.imports, ...data.exports].some((r) => r.status === 'En Cours');

export function useImportExport() {
    return useQuery({
        queryKey: KEY,
        queryFn: () => call(''),
        // Jobs run server-side in Nemo: poll while one is « En Cours ».
        refetchInterval: (query) => (isRunning(query.state.data) ? 1500 : false),
    });
}

export function useImportExportActions() {
    const qc = useQueryClient();
    const onSuccess = (data: ImportExportData) => qc.setQueryData(KEY, data);
    return {
        startImport: useMutation({ mutationFn: (req: ImportRequest) => call('/imports', json(req)), onSuccess }),
        startExport: useMutation({ mutationFn: (format: ImportExportFormat) => call('/exports', json({ format })), onSuccess }),
        restart: useMutation({ mutationFn: ({ kind, id }: { kind: ImportExportKind; id: string }) => call(`/${kind}/${id}/restart`, { method: 'POST' }), onSuccess }),
        stop: useMutation({ mutationFn: ({ kind, id }: { kind: ImportExportKind; id: string }) => call(`/${kind}/${id}/stop`, { method: 'POST' }), onSuccess }),
        remove: useMutation({ mutationFn: ({ kind, id }: { kind: ImportExportKind; id: string }) => call(`/${kind}/${id}`, { method: 'DELETE' }), onSuccess }),
    };
}
